import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FontAwesomeService} from '@services/font-awesome.service';
import {TranslateService} from '@ngx-translate/core';
import {Store} from '@ngrx/store';
import {AccountService} from '@services/account.service';
import {AppState, selectFooterAction, selectUser, ShipmentTypes} from '@store/reducers';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';
import {Router} from '@angular/router';

import {PrintDocumentsService} from '@services/print-documents.service';
import {BrowserPrintService} from '@services/browser-print.service';
import {FooterActionEnum} from '@models/footer-action.enum';

import {DateFormatPipe} from '@pipes/date-format.pipe';
import {CurrencyFormatPipe} from '@pipes/currency-fomat.pipe';
import {PrintFormatEnum, PrintTypeEnum} from '@models/print.enum';
import {User} from '@models/user.interface';
import {MatSnackBar} from '@angular/material';
import {SnackBarComponent} from '@components/snackbar/snackbar.component';
import {environment} from '../../../../../environments/environment';
import 'rxjs/add/operator/map';

@Component({
  selector: 'app-booked-shipments',
  template: `
    <app-shipment-table *ngIf="shipmentsState"
      [shipmentType]="shipmentTypes.BOOKED"
      [rows]="rows"
      [data]="generateData(data)"
      [checkboxSign]="true"
      [deleteSign]="false"
      [totalShipmentsLength]="totalShipmentsLength"
      [currentPageSize]="currentPageSize"
      [currentPage]="currentPage"
      (selectRow)="handleSelectRow($event)"
      (changePage)="handleChangePage($event)"></app-shipment-table>
  `
})
export class BookedShipmentsComponent implements OnInit, OnDestroy {
  rows = [
    {label: this.translate.instant('account.section.shipment.booked.table.header1'), value: 'consignment_no'},
    {label: this.translate.instant('account.section.shipment.booked.table.header2'), value: 'consignor'},
    {label: this.translate.instant('account.section.shipment.booked.table.header3'), value: 'consignee'},
    {label: this.translate.instant('account.section.shipment.booked.table.header4'), value: 'pickup_date'},
    {label: this.translate.instant('account.section.shipment.booked.table.header5'), value: 'price'},
  ];
  private user: User;
  private userPrintSetting;
  private userPrintFormat;
  private unsubscribe: Subject<void> = new Subject();
  shipmentTypes = ShipmentTypes;

  @Input() shipmentsState: any;
  @Input() data: any;
  @Input() currentPage: number;
  @Input() currentPageSize: number;
  @Input() totalShipmentsLength: number;

  @Output() onChangePage: EventEmitter<any> = new EventEmitter();

  constructor(
    private translate: TranslateService,
    public faService: FontAwesomeService,
    private accountService: AccountService,
    private store: Store<AppState>,
    public router: Router,
    private printDocumentsService: PrintDocumentsService,
    private browserPrintService: BrowserPrintService,
    public matSnackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.store.select(selectUser)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data: User) => {
        this.user = data;
        if (data) {
          this.userPrintSetting = data.settings.unifaun_print_settings;
          this.userPrintFormat = data.settings.unifaun_print_format;
        }
      });
    this.store.select(selectFooterAction)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(action => {
        let shipmentsForPrint = [];
        let hasWaybill = [];
        switch (action) {
          case FooterActionEnum.PRINT_LABELS_FOR_SELECTED_SHIPMENTS:
            shipmentsForPrint = this.data.filter(shipment => shipment.selected).map(shipment => shipment.shipment_ref);
            // if (this.userPrintFormat === PrintFormatEnum.ZPL) {
            //   this.browserPrintService.printFreightDocsByConsignmentNumber(shipmentsForPrint, PrintTypeEnum.LABEL);
            // } else {
              this.printDocumentsService.printFreightDocsByConsignmentNumber(
                shipmentsForPrint, PrintTypeEnum.LABEL, this.userPrintFormat, this.userPrintSetting
              );
            // }
            break;

          case FooterActionEnum.PRINT_WAYBILL_FOR_SELECTED_SHIPMENTS:
            hasWaybill = this.data.filter(shipment => shipment.selected).map((shipment) => {
              if (environment.Unifaun.waybillProducts.includes(shipment.product_id)) {
                return shipment.product_id;
              }
            }).filter(Number);

            if (hasWaybill.length === 0) {
              // No waybills
              this.snackBar(this.translate.instant('account.section.shipment.booked.noWaybill'));
            } else {
              // Print waybills
              shipmentsForPrint = this.data.filter(shipment => shipment.selected).map(shipment => shipment.shipment_ref);
              this.printDocumentsService.printFreightDocsByConsignmentNumber(shipmentsForPrint, PrintTypeEnum.WAYBILL);
            }
            break;

          case FooterActionEnum.PRINT_RECEIPT_LIST_FOR_SELECTED_SHIPMENTS:
            shipmentsForPrint = this.data.filter(shipment => shipment.selected).map(shipment => shipment.shipment_ref);
            this.printDocumentsService.printFreightDocsByConsignmentNumber(shipmentsForPrint, PrintTypeEnum.RECEIPT);
            break;

          case FooterActionEnum.PRINT_PICKING_LIST_FOR_SELECTED_SHIPMENTS:
            shipmentsForPrint = this.data.filter(shipment => shipment.selected).map(shipment => shipment.shipment_ref);
            this.printDocumentsService.printFreightDocsByConsignmentNumber(shipmentsForPrint, PrintTypeEnum.PICKING_LIST);
            break;
        }
      });
  }

  generateData(shipments) {
    return shipments.map((ship) => {
      const {
        consignor, consignee, amount, vat, shipment_ref, status, pickup_date, pickup_time_from, pickup_time_to, id, reference, selected, product, additional_services
      } = ship;

      // Calculate additional services cost
      let additionalServicesCost = { amount: 0, vat: 0 };
      if (additional_services) {
        additionalServicesCost = additional_services.reduce((total, service) => {
          const serviceAmount = Number(service.price ? service.price : 0);
          const serviceVat = serviceAmount * (Number(service.vat_rate ? service.vat_rate : 0) / 100);
          return {
            amount: total.amount + serviceAmount,
            vat: total.vat + serviceVat
          };
        }, { amount: 0, vat: 0 });
      }

      // Calculate total price
      const totalAmount = Number(amount) + additionalServicesCost.amount;
      const totalVat = Number(vat) + additionalServicesCost.vat;

      const pickupDate = `${(new DateFormatPipe(this.translate)).transform(pickup_date, 'mediumDate', '', this.translate.currentLang)}`;
      const startTime = pickup_time_from.length === 8 ? pickup_time_from.slice(0, -3) : pickup_time_from;
      const endTime = pickup_time_to.length === 8 ? pickup_time_to.slice(0, -3) : pickup_time_to;
      const timeString = this.translate.instant('common.date.timeLabel') + ' ' + startTime + ' - ' + endTime;

      return {
        consignor: consignor &&
          `${consignor.country_code.toUpperCase()}-${consignor.zip}, ${consignor.city.toUpperCase()}`,
        consignee: consignee &&
          `${consignee.country_code.toUpperCase()}-${consignee.zip}, ${consignee.city.toUpperCase()}`,
        price: `${(new CurrencyFormatPipe(this.translate)
          .transform(totalAmount, 'SEK', 'code', '1.2-2', this.translate.currentLang))}`,
        pickup_date: pickupDate,
        consignment_no: shipment_ref,
        id,
        selected,
        reference,
      };
    });
  }

  handleChangePage(current_page) {
    this.onChangePage.emit(current_page);
  }
  handleSelectRow(id) {
    this.router.navigate(
      ['/shipments/booked/', id]
    );
  }

  private snackBar(text: string, isError: boolean = true): void {
    this.matSnackBar.openFromComponent(SnackBarComponent, {
      data: {
        error: isError,
        message: text
      },
      panelClass: 'snackbar-container',
      duration: 3000,
    });
  }

  ngOnDestroy() {
    // this.printDocumentsService.unsubscribeUser();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}




// WEBPACK FOOTER //
// ./src/app/modules/shipments/components/booked-shipments/booked-shipments.component.ts